import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Zap, Plus, Trash2, Play, Pause, ChevronDown, ChevronUp } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { extractArray } from '@/lib/deliverablesResponse';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function authHeaders(): HeadersInit {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

type CampaignStatus = 'DRAFT' | 'ACTIVE' | 'PAUSED' | 'COMPLETED' | 'CANCELLED';
type TargetAudience =
  | 'ALL_LEADS'
  | 'HOT_LEADS'
  | 'WARM_LEADS'
  | 'COLD_LEADS'
  | 'WON_LEADS'
  | 'LOST_LEADS';

interface TargetAudienceFilter {
  stages?: string[];
  priorities?: string[];
  lastActivityDays?: number;
}

interface Campaign {
  id: string;
  name: string;
  description?: string;
  status: CampaignStatus;
  targetAudience: TargetAudience | TargetAudienceFilter;
  scheduledAt?: string | null;
  customMessage: string | null;
  createdAt: string;
  messagesSent?: number;
  totalRecipients?: number;
  sentCount?: number;
}

interface CreateCampaignPayload {
  name: string;
  description?: string;
  customMessage: string;
  targetAudience: TargetAudience;
  scheduledAt?: string;
  status: 'DRAFT';
}

async function fetchCampaigns(): Promise<Campaign[]> {
  const res = await fetch(`${API_BASE}/whatsapp/campaigns?limit=50&skip=0`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error('Failed to fetch campaigns');
  const json = await res.json();
  // Backend returns { success, data: Campaign[], meta }, but the array may also
  // arrive as a bare array or nested under { data: { campaigns: [...] } }.
  return extractArray<Campaign>(json, 'campaigns');
}

async function createCampaign(payload: CreateCampaignPayload): Promise<Campaign> {
  const res = await fetch(`${API_BASE}/whatsapp/campaigns`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to create campaign');
  }
  const json = await res.json();
  return json.data as Campaign;
}

async function startCampaign(id: string): Promise<void> {
  const res = await fetch(`${API_BASE}/whatsapp/campaigns/${id}/start`, {
    method: 'POST',
    headers: authHeaders(),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to start campaign');
  }
}

async function pauseCampaign(id: string): Promise<void> {
  const res = await fetch(`${API_BASE}/whatsapp/campaigns/${id}/pause`, {
    method: 'POST',
    headers: authHeaders(),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to pause campaign');
  }
}

async function deleteCampaign(id: string): Promise<void> {
  const res = await fetch(`${API_BASE}/whatsapp/campaigns/${id}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to delete campaign');
  }
}

const STATUS_BADGE_CLASSES: Record<CampaignStatus, string> = {
  DRAFT: 'bg-gray-100 text-gray-800',
  ACTIVE: 'bg-green-100 text-green-800',
  PAUSED: 'bg-yellow-100 text-yellow-800',
  COMPLETED: 'bg-blue-100 text-blue-800',
  CANCELLED: 'bg-red-100 text-red-800',
};

const AUDIENCE_LABELS: Record<TargetAudience, string> = {
  ALL_LEADS: 'All Leads',
  HOT_LEADS: 'Hot Leads',
  WARM_LEADS: 'Warm Leads',
  COLD_LEADS: 'Cold Leads',
  WON_LEADS: 'Won Leads',
  LOST_LEADS: 'Lost Leads',
};

function formatTargetAudience(audience: TargetAudience | TargetAudienceFilter): string {
  if (typeof audience === 'string') {
    return AUDIENCE_LABELS[audience as TargetAudience] || audience;
  }
  if (typeof audience === 'object' && audience !== null) {
    const parts: string[] = [];
    if ((audience as TargetAudienceFilter).stages?.length) {
      parts.push((audience as TargetAudienceFilter).stages!.join(', '));
    }
    if ((audience as TargetAudienceFilter).priorities?.length) {
      parts.push((audience as TargetAudienceFilter).priorities!.join(', '));
    }
    return parts.length > 0 ? parts.join(' · ') : 'Custom';
  }
  return 'All Leads';
}

const DEFAULT_FORM: CreateCampaignPayload = {
  name: '',
  customMessage: '',
  targetAudience: 'ALL_LEADS',
  scheduledAt: '',
  status: 'DRAFT',
};

export const WhatsAppCampaigns: React.FC = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<CreateCampaignPayload>(DEFAULT_FORM);
  const [formErrors, setFormErrors] = useState<{ name?: string; customMessage?: string }>({});

  const { data: campaigns, isLoading, error } = useQuery<Campaign[], Error>({
    queryKey: ['whatsapp-campaigns'],
    queryFn: fetchCampaigns,
  });

  const createMutation = useMutation<Campaign, Error, CreateCampaignPayload>({
    mutationFn: createCampaign,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['whatsapp-campaigns'] });
      setForm(DEFAULT_FORM);
      setShowForm(false);
      toast({ title: 'Campaign created', description: 'Campaign saved as draft.' });
    },
    onError: (err) => {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    },
  });

  const startMutation = useMutation<void, Error, string>({
    mutationFn: startCampaign,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['whatsapp-campaigns'] });
      toast({ title: 'Campaign started', description: 'Campaign is now active.' });
    },
    onError: (err) => {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    },
  });

  const pauseMutation = useMutation<void, Error, string>({
    mutationFn: pauseCampaign,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['whatsapp-campaigns'] });
      toast({ title: 'Campaign paused', description: 'Campaign has been paused.' });
    },
    onError: (err) => {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    },
  });

  const deleteMutation = useMutation<void, Error, string>({
    mutationFn: deleteCampaign,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['whatsapp-campaigns'] });
      toast({ title: 'Campaign deleted', description: 'Campaign has been removed.' });
    },
    onError: (err) => {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    },
  });

  function handleFormChange(
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name as keyof typeof formErrors]) {
      setFormErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const errors: { name?: string; customMessage?: string } = {};
    if (!form.name.trim()) errors.name = 'Name is required';
    if (!form.customMessage.trim()) errors.customMessage = 'Message is required';
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    const payload: CreateCampaignPayload = {
      name: form.name.trim(),
      customMessage: form.customMessage.trim(),
      targetAudience: form.targetAudience,
      status: 'DRAFT',
    };
    if (form.scheduledAt) payload.scheduledAt = form.scheduledAt;
    createMutation.mutate(payload);
  }

  function handleDelete(id: string) {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm('Delete this campaign?')) return;
    deleteMutation.mutate(id);
  }

  function formatDate(dateStr?: string | null): string {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center space-x-2">
            <Zap className="h-5 w-5" />
            <span>Campaigns</span>
          </CardTitle>
          <Button
            size="sm"
            onClick={() => setShowForm((prev) => !prev)}
            aria-expanded={showForm}
          >
            {showForm ? (
              <>
                <ChevronUp className="h-4 w-4 mr-2" />
                Cancel
              </>
            ) : (
              <>
                <Plus className="h-4 w-4 mr-2" />
                New Campaign
              </>
            )}
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Inline create form */}
        {showForm && (
          <form
            onSubmit={handleSubmit}
            className="border border-gray-200 rounded-lg p-4 bg-gray-50 space-y-4"
          >
            <h3 className="font-semibold text-gray-900">New Campaign</h3>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Campaign Name <span className="text-red-500">*</span>
              </label>
              <Input
                name="name"
                value={form.name}
                onChange={handleFormChange}
                placeholder="e.g. March Follow-up Campaign"
              />
              {formErrors.name && (
                <p className="text-red-500 text-xs mt-1">{formErrors.name}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Message <span className="text-red-500">*</span>
              </label>
              <textarea
                name="customMessage"
                value={form.customMessage}
                onChange={handleFormChange}
                rows={4}
                placeholder="Write your WhatsApp message here..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {formErrors.customMessage && (
                <p className="text-red-500 text-xs mt-1">{formErrors.customMessage}</p>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Target Audience
                </label>
                <select
                  name="targetAudience"
                  value={form.targetAudience}
                  onChange={handleFormChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {(Object.keys(AUDIENCE_LABELS) as TargetAudience[]).map((key) => (
                    <option key={key} value={key}>
                      {AUDIENCE_LABELS[key]}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Schedule Date (optional)
                </label>
                <Input
                  type="datetime-local"
                  name="scheduledAt"
                  value={form.scheduledAt || ''}
                  onChange={handleFormChange}
                />
              </div>
            </div>

            <div className="flex gap-3">
              <Button type="submit" disabled={createMutation.isPending} size="sm">
                {createMutation.isPending ? 'Creating...' : 'Create Campaign'}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowForm(false);
                  setForm(DEFAULT_FORM);
                  setFormErrors({});
                }}
              >
                Cancel
              </Button>
            </div>
          </form>
        )}

        {/* Campaign list */}
        {isLoading ? (
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="animate-pulse h-16 bg-gray-100 rounded-lg" />
            ))}
          </div>
        ) : error ? (
          <div className="text-center py-10 text-red-500">
            <p>Failed to load campaigns. Please try again.</p>
          </div>
        ) : campaigns && campaigns.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-3 font-medium text-gray-600">Name</th>
                  <th className="text-left py-3 px-3 font-medium text-gray-600">Status</th>
                  <th className="text-left py-3 px-3 font-medium text-gray-600">Audience</th>
                  <th className="text-left py-3 px-3 font-medium text-gray-600">Scheduled</th>
                  <th className="text-left py-3 px-3 font-medium text-gray-600">Sent</th>
                  <th className="text-right py-3 px-3 font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((campaign) => (
                  <tr
                    key={campaign.id}
                    className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                  >
                    <td className="py-3 px-3">
                      <p className="font-medium text-gray-900">{campaign.name}</p>
                      {campaign.description && (
                        <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">
                          {campaign.description}
                        </p>
                      )}
                    </td>
                    <td className="py-3 px-3">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE_CLASSES[campaign.status]}`}
                      >
                        {campaign.status}
                      </span>
                    </td>
                    <td className="py-3 px-3 text-gray-600">
                      {formatTargetAudience(campaign.targetAudience)}
                    </td>
                    <td className="py-3 px-3 text-gray-600">{formatDate(campaign.scheduledAt)}</td>
                    <td className="py-3 px-3 text-gray-600">
                      {campaign.messagesSent ?? 0}
                      {campaign.totalRecipients != null && (
                        <span className="text-gray-400"> / {campaign.totalRecipients}</span>
                      )}
                    </td>
                    <td className="py-3 px-3">
                      <div className="flex items-center justify-end gap-2">
                        {campaign.status === 'DRAFT' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => startMutation.mutate(campaign.id)}
                            disabled={startMutation.isPending}
                            title="Start campaign"
                          >
                            <Play className="h-3 w-3" />
                          </Button>
                        )}
                        {campaign.status === 'ACTIVE' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => pauseMutation.mutate(campaign.id)}
                            disabled={pauseMutation.isPending}
                            title="Pause campaign"
                          >
                            <Pause className="h-3 w-3" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDelete(campaign.id)}
                          disabled={deleteMutation.isPending}
                          title="Delete campaign"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <Zap className="h-12 w-12 mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500 font-medium">No campaigns yet</p>
            <p className="text-gray-400 text-sm mt-1">
              Create your first campaign to reach leads in bulk.
            </p>
            <Button className="mt-4" size="sm" onClick={() => setShowForm(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Campaign
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
